import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Printer } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CoalitionChapter } from "@/components/strategy/chapters/coalition-chapter";
import { JourneyChapter } from "@/components/strategy/chapters/journey-chapter";
import { LandscapeChapter } from "@/components/strategy/chapters/landscape-chapter";
import { MathChapter } from "@/components/strategy/chapters/math-chapter";
import { PositionChapter } from "@/components/strategy/chapters/position-chapter";
import { TomorrowChapter } from "@/components/strategy/chapters/tomorrow-chapter";
import { VotersChapter } from "@/components/strategy/chapters/voters-chapter";
import { ConfidenceIndicator } from "@/components/strategy/confidence-indicator";
import { CHAPTERS, StrategyToc } from "@/components/strategy/strategy-toc";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useStrategy, useStrategyStatus } from "@/hooks/use-strategy";

export const Route = createFileRoute("/_app/_/strategy/brief")({
	component: StrategyBriefPage,
});

// ---------------------------------------------------------------------------
// Chapter renderer mapping
// ---------------------------------------------------------------------------

const CHAPTER_COMPONENTS: Record<
	string,
	React.ComponentType<{
		data: Record<string, unknown>;
		sources?: Array<{ claim_id: string; source: string; quote?: string }>;
	}>
> = {
	landscape: LandscapeChapter,
	voters: VotersChapter,
	math: MathChapter,
	position: PositionChapter,
	coalition: CoalitionChapter,
	journey: JourneyChapter,
	tomorrow: TomorrowChapter,
};

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

function StrategyBriefPage() {
	const navigate = useNavigate();
	const { data: statusData, isLoading: statusLoading } = useStrategyStatus();
	const { data: strategyData, isLoading: strategyLoading } = useStrategy();

	const isReady = statusData?.status === "ready";

	const strategyReveal = strategyData?.strategy_reveal as
		| Record<string, unknown>
		| undefined;
	const confidenceLevel = strategyData?.overall_confidence_level as
		| "high"
		| "medium"
		| "low"
		| undefined;

	// ---------------------------------------------------------------------------
	// IntersectionObserver for active chapter tracking
	// ---------------------------------------------------------------------------

	const chapterRefs = useRef<Record<string, HTMLElement | null>>({});
	const [activeChapter, setActiveChapter] = useState(CHAPTERS[0].key);

	const hasRevealData = strategyReveal != null;

	useEffect(() => {
		if (!hasRevealData) return;

		const elements = Object.values(chapterRefs.current).filter(Boolean);
		if (elements.length === 0) return;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						const key = entry.target.getAttribute("data-chapter");
						if (key) {
							setActiveChapter(key);
						}
					}
				}
			},
			{
				rootMargin: "-20% 0px -60% 0px",
				threshold: 0,
			},
		);

		for (const el of elements) {
			if (el) observer.observe(el);
		}

		return () => observer.disconnect();
	}, [hasRevealData]);

	const handleChapterSelect = useCallback((key: string) => {
		const el = chapterRefs.current[key];
		if (el) {
			el.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	}, []);

	const chapterDataMap = useMemo(() => {
		if (!strategyReveal) return {};
		const map: Record<string, Record<string, unknown>> = {};
		for (const ch of CHAPTERS) {
			map[ch.key] = (strategyReveal[ch.key] as Record<string, unknown>) ?? {};
		}
		return map;
	}, [strategyReveal]);

	const isLoading = statusLoading || strategyLoading;

	// Redirect if not ready (after all hooks)
	useEffect(() => {
		if (!statusLoading && !isReady) {
			navigate({ to: "/strategy", replace: true });
		}
	}, [statusLoading, isReady, navigate]);

	if (!statusLoading && !isReady) {
		return null;
	}

	if (isLoading) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-7xl">
					<div className="flex gap-8">
						<div className="hidden w-64 shrink-0 lg:block">
							<div className="space-y-2">
								{[1, 2, 3, 4, 5, 6, 7].map((n) => (
									<Skeleton key={`skel-${n}`} className="h-10 w-full" />
								))}
							</div>
						</div>
						<div className="flex-1 space-y-6">
							<Skeleton className="h-8 w-48" />
							<Skeleton className="h-6 w-96" />
							<Skeleton className="h-64 w-full" />
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted/30">
			{/* Top bar */}
			<div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur print:hidden">
				<div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
					<Button variant="ghost" size="sm" asChild>
						<a href="/strategy">
							<ChevronLeft data-icon="inline-start" className="size-4" />
							Back to Dashboard
						</a>
					</Button>
					<div className="flex items-center gap-3">
						{confidenceLevel && <ConfidenceIndicator level={confidenceLevel} />}
						<Button variant="outline" size="sm" onClick={() => window.print()}>
							<Printer data-icon="inline-start" className="size-4" />
							Print
						</Button>
					</div>
				</div>
			</div>

			<div className="mx-auto max-w-7xl px-6 py-8">
				<div className="flex gap-8">
					{/* Desktop sidebar TOC */}
					<aside className="hidden w-64 shrink-0 lg:block print:hidden">
						<div className="sticky top-20">
							<h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
								Table of Contents
							</h3>
							<StrategyToc
								chapters={CHAPTERS}
								activeChapter={activeChapter}
								onChapterSelect={handleChapterSelect}
								variant="sidebar"
							/>
						</div>
					</aside>

					{/* Main content — all chapters sequentially */}
					<main className="min-w-0 flex-1">
						<div className="space-y-12">
							{CHAPTERS.map((ch) => {
								const ChapterComponent = CHAPTER_COMPONENTS[ch.key];
								if (!ChapterComponent) return null;
								return (
									<section
										key={ch.key}
										ref={(el) => {
											chapterRefs.current[ch.key] = el;
										}}
										data-chapter={ch.key}
										id={`chapter-${ch.key}`}
										className="scroll-mt-24 rounded-lg border bg-background p-6 shadow-sm lg:p-10"
									>
										<ChapterComponent data={chapterDataMap[ch.key] ?? {}} />
									</section>
								);
							})}
						</div>

						{/* Bottom nav */}
						<div className="mt-8 flex justify-center print:hidden">
							<Button variant="outline" asChild>
								<a href="/strategy">Back to Dashboard</a>
							</Button>
						</div>
					</main>
				</div>
			</div>
		</div>
	);
}
